"use client";
import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/navigation";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckIcon from "@mui/icons-material/Check";
import { Check } from "lucide-react";
const ATLAS_FEATURES = ["feature1", "feature2", "feature3", "feature4"];
import NextLink from "next/link";
const AtlasAgentPage = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const router = useRouter();

  return (
    <Box
    id="atlas-agent-section"
      sx={{
        backgroundImage:
          "url(https://getgeek-website-assets.b-cdn.net/AtlasNewBg.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        overflow: "hidden",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 2.5, sm: 3, md: 4 },
          py: { xs: 6, md: 10 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "flex-start" },
            gap: { xs: 4, md: 7 },
          }}
        >
          {/* Left column: Agent avatar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flexShrink: 0,
              width: { xs: "auto", md: 200 },
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 600,
                fontSize: "0.7rem",
                letterSpacing: "0.2em",
                color: "#AFC8DA",
                mb: 2,
                textTransform: "uppercase",
              }}
            >
              {t("atlasPage.agentLabel", "Agent 04")}
            </Typography>

            <Box
              component="img"
              src="https://getgeek-website-assets.b-cdn.net/AtlasLogoNew.webp"
              alt="Atlas"
              sx={{
                width: { xs: "110px", sm: "130px", md: "150px" },
                height: "auto",
                objectFit: "cover",
                display: "block",
                mb: 2,
              }}
            />

            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.15rem" },
                letterSpacing: "0.15em",
                color: "#101828",
                textAlign: "center",
              }}
            >
              {t("atlasPage.agentName")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                textAlign: "center",
                fontWeight: 500,
                color: "#1D8FDC",
                mb: 1.2,
              }}
            >
              {t("atlasPage.agentRole")}
            </Typography>

            <Box
              sx={{
                width: 28,
                height: 3,
                borderRadius: "2px",
                background: "linear-gradient(90deg, #6192FF 0%, #04BFEF 100%)",
              }}
            />
          </Box>

          {/* Right column: Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Main Heading */}
            <Typography
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 700,
                fontSize: { xs: "1.4rem", sm: "1.7rem", md: "2.1rem" },
                color: "#101828",
                lineHeight: 1.3,
                mb: 3,
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("atlasPage.headingPart1")}{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #1D8FDC 0%, #1D8FDC 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {t("atlasPage.headingPart2")}
              </Box>
            </Typography>

            {/* Alert Banner */}
            <Box
              sx={{
                mb: 3,
                borderLeft: isArabic ? "none" : "3px solid #1D8FDC",
                borderRight: isArabic ? "3px solid #1D8FDC" : "none",
                pl: isArabic ? 0 : 1.8,
                pr: isArabic ? 1.8 : 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.92rem" },
                  color: "#14122B",
                  lineHeight: 1.85,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    backgroundColor: "#E4F2FD",
                    fontWeight: 700,
                    color: "#101828",
                    px: 0.6,
                    py: 0.15,
                    borderRadius: "4px",
                  }}
                >
                  {t("atlasPage.alertBold1")}
                </Box>{" "}
                {t("atlasPage.alertText1")}{" "}
                {t("atlasPage.alertText2")}{" "}
                {/* <Box
                  component="span"
                  sx={{
                    backgroundColor: "#DAEEFF",
                    fontWeight: 700,
                    color: "#101828",
                    px: 0.6,
                    py: 0.15,
                    borderRadius: "4px",
                  }}
                >
                  {t("atlasPage.alertBold2")}
                </Box>{" "} */}
                {/* {t("atlasPage.alertText3")} */}
              </Typography>
            </Box>

            {/* Description paragraphs */}
            {["desc1", "desc2", "desc3"].map((key) => {
              const value = t(`atlasPage.${key}`);
              if (!value) return null;
              return (
                <Typography
                  key={key}
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.92rem" },
                    color: "#6B7280",
                    lineHeight: 1.85,
                    mb: 1.8,
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {value}
                </Typography>
              );
            })}

            {/* What ATLAS does for you */}
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 700,
                fontSize: { xs: "0.85rem", sm: "0.92rem", md: "0.98rem" },
                color: "#101828",
                mt: { xs: 2, sm: 2.5 },
                mb: 1.8,
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("atlasPage.featuresTitle")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 1.2, sm: 1.5 },
                mb: 3,
              }}
            >
              {ATLAS_FEATURES.map((key) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    backgroundColor: "#F5FAFF",
                    border: "1px solid #DCE9FB",
                    borderRadius: "10px",
                    p: 1.4,
                  }}
                >

                  <Check
                    size={16}
                    strokeWidth={3}   // 2 = normal, 3 = bold, 4 = extra bold
                    color="#1D8FDC"
                    style={{ flexShrink: 0, marginTop: 3 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      color: "#374151",
                      lineHeight: 1.6,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t(`atlasPage.features.${key}`)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Highlight box */}
            <Box
              sx={{
                mb: 4,
                backgroundColor: "#F5FAFF",
                border: "1px solid #DCE9FB",
                borderRadius: "10px",
                p: 1.8,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.75rem", sm: "0.82rem", md: "0.88rem" },
                  color: "#101828",
                  lineHeight: 1.85,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {t("atlasPage.highlightBold1")}
                </Box>{" "}
                {t("atlasPage.highlightText1")}{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {t("atlasPage.highlightBold2")}
                </Box>{" "}
                {t("atlasPage.highlightText2")}
              </Typography>
            </Box>

            {/* CTA Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                pb: { xs: 4, sm: 5 },
              }}
            >
              <NextLink href="/ai-agents/atlas" passHref legacyBehavior>
                <Button
                  component="a"
                  rel="noopener noreferrer"
                  sx={{
                    background: "linear-gradient(90deg, #1D8FDC 0%, #1D8FDC 100%)",
                    color: "#fff",
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 600,
                    fontSize: { xs: "0.82rem", sm: "0.88rem" },
                    px: { xs: 3, sm: 3.5 },
                    py: { xs: 1.1, sm: 1.3 },
                    borderRadius: "50px",
                    textTransform: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(90deg, #1D8FDC 0%, #1D8FDC 100%)",
                      transform: "scale(1.03)",
                    },
                    "&:active": { transform: "scale(0.98)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <span>{t("atlasPage.ctaButton")}</span>
                    {isArabic ? (
                      <ArrowBackIosNewIcon sx={{ fontSize: "0.75rem" }} />
                    ) : (
                      <ArrowForwardIosIcon sx={{ fontSize: "0.75rem" }} />
                    )}
                  </Box>
                </Button>
              </NextLink>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AtlasAgentPage;